import { Box, type BoxProps, Button, Text, Tooltip } from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import type { RepetitionSettingsDaysEnum } from "@src/__generated__/graphql";
import type { UserAllocatedTime } from "@src/components/ui-kit/allocated-time-indicator/store";
import { UsersAllocatedTimeStore } from "@src/components/ui-kit/allocated-time-indicator/store";
import type { AppStore } from "@src/stores/AppStore";
import { useStore } from "@src/utils/hooks";
import { secondsToHoursWithMinutes, timeToSeconds } from "@src/utils/time";
import type { Time } from "@src/utils/types";
import { type CalendarDate, useCalendarDay } from "@uselessdev/datepicker";
import { format, isSameDay, isWithinInterval } from "date-fns";
import { action, computed, makeObservable, observable } from "mobx";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useEffect } from "react";

export type AllocatedTimeBaseProps = {
  variant?: "allocation" | "task";
  from: Date | undefined;
  to: Date | undefined;
  fromTime?: Time | undefined;
  toTime?: Time | undefined;
  reccurentDays?: RepetitionSettingsDaysEnum[];
};

interface Props extends AllocatedTimeBaseProps {
  userAllocatedTime: UserAllocatedTime | undefined;
}
export const AllocatedTimeCalendarDay = observer(
  function AllocatedTimeCalendarDay({
    userAllocatedTime,
    variant = "allocation",
    from,
    to,
    fromTime,
    toTime,
    reccurentDays,
  }: Props) {
    const appStore = useStore();
    const { day } = useCalendarDay();
    const store = useLocalObservable(
      () => new AllocatedTimeCalendarDayStore(appStore, day),
    );

    useEffect(() => {
      store.setUserAllocatedTime(userAllocatedTime);
    }, [store, userAllocatedTime]);

    return (
      <Tooltip
        hasArrow
        isDisabled={store.isTooltipDisabled}
        label={
          <Box>
            {variant === "allocation" && (
              <Text mb="2" fontWeight="medium">
                <Trans>Total capacity:</Trans>&nbsp;
                {secondsToHoursWithMinutes(
                  store.dayData?.planned_capacity ?? 0,
                )}
              </Text>
            )}
            {store.tooltipLabel?.map((label) => (
              <Text key={label}>{label}</Text>
            ))}
            {variant === "allocation" && (
              <Text mt="2" fontWeight="medium">
                <Trans>Left to allocate:</Trans>&nbsp;
                {secondsToHoursWithMinutes(store.capacityAvailable)}
              </Text>
            )}
          </Box>
        }
        placement="top"
      >
        <Button pos="relative" colorScheme="black" variant="ghost">
          <Box
            pos="absolute"
            top="0"
            right={store.boxRight}
            bottom="0"
            left="0"
            bg={store.getDayColor(
              day,
              from,
              to,
              fromTime,
              toTime,
              reccurentDays,
            )}
            roundedLeft="md"
            roundedRight={store.boxRoundedRight}
          />

          <Text zIndex="1" color="black">
            {store.dayLabel}
          </Text>
        </Button>
      </Tooltip>
    );
  },
);

class AllocatedTimeCalendarDayStore {
  appStore: AppStore;

  @observable.ref
  day: CalendarDate;

  @observable.ref
  userAllocatedTime: UserAllocatedTime | undefined;

  constructor(appStore: AppStore, day: CalendarDate) {
    makeObservable(this);
    this.appStore = appStore;
    this.day = day;
  }

  @action.bound
  setUserAllocatedTime(userAllocatedTime: UserAllocatedTime | undefined) {
    this.userAllocatedTime = userAllocatedTime;
  }

  @computed
  get dayData() {
    return this.userAllocatedTime?.dates?.find(({ date }) =>
      isSameDay(date, this.day),
    );
  }

  @computed
  get totalAllocatedTime(): number {
    return (
      this.dayData?.data?.reduce((curr, { value }) => curr + (value ?? 0), 0) ??
      0
    );
  }

  @computed
  get capacityAvailable(): number {
    if (!this.dayData) return 0;
    return this.dayData.planned_capacity - this.totalAllocatedTime;
  }

  @computed
  get capacityUserPercentage() {
    if (!this.dayData) return 0;
    return (this.totalAllocatedTime / this.dayData.planned_capacity) * 100;
  }

  @computed
  get tooltipLabel() {
    return this.dayData?.data?.map((item) =>
      UsersAllocatedTimeStore.formatRow(item),
    );
  }

  @computed
  get isTooltipDisabled() {
    return !(this.dayData?.data?.length ?? 0);
  }

  @computed
  get boxRight() {
    if (this.capacityUserPercentage >= 100) return "0%";
    if (this.capacityUserPercentage <= 0) return "100%";
    return `${100 - this.capacityUserPercentage}%`;
  }

  @computed
  get boxRoundedRight() {
    return this.capacityUserPercentage >= 100 ? "md" : "none";
  }

  @computed
  get dayLabel() {
    return format(this.day, "d");
  }

  getDayColor(
    day: CalendarDate,
    from: Date | undefined,
    to: Date | undefined,
    fromTime: Time | undefined,
    toTime: Time | undefined,
    reccurentDays?: RepetitionSettingsDaysEnum[],
  ): BoxProps["bg"] {
    if (!this.isInDateRange(day, from, to)) {
      return "gray.200";
    }

    if (!this.dayData?.data?.length) {
      return "gray.200";
    }

    const isWeekdaySelected =
      reccurentDays && reccurentDays.length > 0
        ? reccurentDays.includes(this.getDayOfWeekEnum(day))
        : true;

    const hasTimeOverlap =
      fromTime && toTime ? this.hasTimeOverlap(fromTime, toTime) : true;

    return isWeekdaySelected && hasTimeOverlap ? "red.400" : "gray.200";
  }

  private resetHours(day: CalendarDate | Date): Date {
    const date = new Date(day);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private isInDateRange(
    day: CalendarDate,
    from: Date | undefined,
    to: Date | undefined,
  ): boolean {
    if (!from || !to) return true;
    if (isSameDay(day, from) && isSameDay(day, to)) return true;
    return isWithinInterval(this.resetHours(day), {
      start: this.resetHours(from),
      end: this.resetHours(to),
    });
  }

  private hasTimeOverlap(fromTime: Time, toTime: Time): boolean {
    if (!this.dayData?.data) return false;

    const fromTimeSeconds = timeToSeconds(fromTime);
    const toTimeSeconds = timeToSeconds(toTime);

    if (fromTimeSeconds === undefined || toTimeSeconds === undefined) {
      return false;
    }

    return this.dayData.data.some((allocation) => {
      // If allocation doesn't have time range, consider it as overlapping
      if (!allocation.from_time || !allocation.to_time) {
        return true;
      }

      const allocationFromSeconds = timeToSeconds(
        allocation.from_time.slice(0, 5) as Time,
      );
      const allocationToSeconds = timeToSeconds(
        allocation.to_time.slice(0, 5) as Time,
      );

      if (
        allocationFromSeconds === undefined ||
        allocationToSeconds === undefined
      ) {
        return true;
      }

      // Check if time ranges overlap
      return (
        fromTimeSeconds < allocationToSeconds &&
        toTimeSeconds > allocationFromSeconds
      );
    });
  }

  private getDayOfWeekEnum(day: CalendarDate): RepetitionSettingsDaysEnum {
    return format(day, "EEEE").toUpperCase() as RepetitionSettingsDaysEnum;
  }
}
